import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { PADDLES } from '../../data/technicals';

const RED = PADDLES.hexcore.accent;
const BLUE = PADDLES.profoam.accent;

// Schematic flex character with an interactive load window: light touch (1–3 kg) vs hard shot (5–10 kg)
const WINDOWS = {
  light: { from: 1, to: 3, label: 'Soft shots · 1–3 kg', ratio: '1.6×', text: 'On soft shots ProFoam bends about 1.6× more than HexCore — HexCore feels firmer on dinks and resets.' },
  hard: { from: 5, to: 10, label: 'Hard shots · 5–10 kg', ratio: '0.8×', text: 'On hard shots ProFoam stiffens up and bends slightly less than HexCore (about 0.8×). HexCore bends the same way at every load.' },
};

export const FlexCurve = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const [win, setWin] = useState('light');
  const w = WINDOWS[win];
  const W = 560, H = 300, px = 56, py = 24;
  const x = (kg) => px + (kg / 10) * (W - px - 16);
  const y = (mm) => H - py - (mm / 5) * (H - py - 24);
  const hex = `M ${x(0)} ${y(0)} L ${x(10)} ${y(3.6)}`;
  const pro = `M ${x(0)} ${y(0)} C ${x(2.5)} ${y(2.4)}, ${x(5)} ${y(3.3)}, ${x(10)} ${y(4.1)}`;
  const draw = (delay) => ({
    initial: { pathLength: 0, opacity: 0 },
    animate: inView ? { pathLength: 1, opacity: 1 } : {},
    transition: { duration: 1.6, ease: 'easeInOut', delay },
  });

  return (
    <div ref={ref} className="relative" data-testid="flex-curve">
      <div className="inline-flex rounded-full border border-white/15 p-1 mb-4" data-testid="flex-window-toggle">
        {Object.entries(WINDOWS).map(([k, v]) => (
          <button key={k} onClick={() => setWin(k)} className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${win === k ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'}`} data-testid={`flex-window-${k}`}>
            {v.label.split(' · ')[0]}<span className="hidden sm:inline"> · {v.label.split(' · ')[1]}</span>
          </button>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Schematic flex response: HexCore near-linear, ProFoam progressive">
        <defs>
          <linearGradient id="fc-red" x1="0" x2="1"><stop offset="0" stopColor={RED} stopOpacity="0.6" /><stop offset="1" stopColor={RED} /></linearGradient>
          <linearGradient id="fc-blue" x1="0" x2="1"><stop offset="0" stopColor={BLUE} stopOpacity="0.6" /><stop offset="1" stopColor={BLUE} /></linearGradient>
        </defs>
        {[0, 1, 2, 3, 4, 5].map((mm) => (
          <line key={mm} x1={px} x2={W - 16} y1={y(mm)} y2={y(mm)} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        ))}
        {[0, 1, 3, 5, 10].map((kg) => (
          <g key={kg}>
            <line x1={x(kg)} x2={x(kg)} y1={y(0)} y2={y(0) + 6} stroke="rgba(255,255,255,0.3)" />
            <text x={x(kg)} y={H - 4} textAnchor="middle" className="fill-zinc-500" style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{kg} kg</text>
          </g>
        ))}
        {/* active load window */}
        <motion.rect y={py} height={H - py - 24} fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.18)" strokeDasharray="3 3"
          initial={false} animate={{ x: x(w.from), width: x(w.to) - x(w.from) }} transition={{ type: 'spring', stiffness: 160, damping: 24 }} />
        <motion.text y={py + 14} textAnchor="middle" fill="rgba(255,255,255,0.75)" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', letterSpacing: 1.5 }}
          initial={false} animate={{ x: (x(w.from) + x(w.to)) / 2 }} transition={{ type: 'spring', stiffness: 160, damping: 24 }}>
          PROFOAM BENDS {w.ratio} HEXCORE
        </motion.text>
        <text x={14} y={y(2.5)} transform={`rotate(-90 14 ${y(2.5)})`} textAnchor="middle" className="fill-zinc-500" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', letterSpacing: 2 }}>DEFLECTION →</text>
        <motion.path d={pro} fill="none" stroke="url(#fc-blue)" strokeWidth="3.5" strokeLinecap="round" {...draw(0.2)} />
        <motion.path d={hex} fill="none" stroke="url(#fc-red)" strokeWidth="3.5" strokeLinecap="round" {...draw(0)} />
        <motion.g initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 1.5 }}>
          <circle cx={x(10)} cy={y(3.6)} r="5" fill={RED} />
          <text x={x(10) - 10} y={y(3.6) + 22} textAnchor="end" fill={RED} style={{ fontSize: 12, fontWeight: 700 }}>HexCore · near-linear</text>
          <circle cx={x(10)} cy={y(4.1)} r="5" fill={BLUE} />
          <text x={x(10) - 10} y={y(4.1) - 12} textAnchor="end" fill={BLUE} style={{ fontSize: 12, fontWeight: 700 }}>ProFoam · stiffens with load</text>
        </motion.g>
      </svg>
      <p className="mt-3 text-sm text-zinc-300 leading-relaxed" data-testid="flex-window-text">{w.text}</p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Illustration based on measured bending from 1 to 10 kg</p>
    </div>
  );
};

// Impact-sound centroid: both spectra overlaid on ONE shared frequency axis so they can be compared directly
export const SoundSignature = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const W = 560, H = 276, px = 40, pt = 50, pb = 44;
  const lo = 300, hi = 2390;
  const x = (hz) => px + ((hz - lo) / (hi - lo)) * (W - px - 16);
  const base = H - pb;
  const curve = (centroid, width, peak) => {
    const pts = [];
    for (let i = 0; i <= 80; i++) {
      const hz = lo + (i / 80) * (hi - lo);
      const d = (hz - centroid) / width;
      const amp = Math.exp(-d * d) * peak + 0.03 * peak * Math.sin(i * 0.9) * Math.exp(-d * d);
      pts.push([x(hz), base - amp * (base - pt)]);
    }
    const line = pts.map(([a, b], i) => `${i ? 'L' : 'M'} ${a.toFixed(1)} ${b.toFixed(1)}`).join(' ');
    return { line, area: `${line} L ${x(hi).toFixed(1)} ${base} L ${x(lo).toFixed(1)} ${base} Z` };
  };
  const hex = curve(785, 330, 1);
  const pro = curve(908, 400, 0.86);
  const ticks = [300, 785, 908, 1500, 2390];
  const draw = (delay) => ({ initial: { pathLength: 0, opacity: 0 }, animate: inView ? { pathLength: 1, opacity: 1 } : {}, transition: { duration: 1.5, ease: 'easeInOut', delay } });
  const fill = (delay) => ({ initial: { opacity: 0 }, animate: inView ? { opacity: 1 } : {}, transition: { duration: 1, delay } });

  return (
    <div ref={ref} data-testid="sound-signature">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Impact sound spectra: HexCore centroid 785 Hz, ProFoam centroid 908 Hz">
        <defs>
          <linearGradient id="ss-red" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={RED} stopOpacity="0.55" /><stop offset="1" stopColor={RED} stopOpacity="0.02" /></linearGradient>
          <linearGradient id="ss-blue" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={BLUE} stopOpacity="0.5" /><stop offset="1" stopColor={BLUE} stopOpacity="0.02" /></linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={px} x2={W - 16} y1={base - f * (base - pt)} y2={base - f * (base - pt)} stroke="rgba(255,255,255,0.06)" />
        ))}
        <line x1={px} x2={W - 16} y1={base} y2={base} stroke="rgba(255,255,255,0.25)" />
        {ticks.map((hz) => (
          <g key={hz}>
            <line x1={x(hz)} x2={x(hz)} y1={base} y2={base + 5} stroke="rgba(255,255,255,0.3)" />
            <text x={x(hz)} y={base + 18} textAnchor="middle" className="fill-zinc-500" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>{hz}</text>
          </g>
        ))}
        <text x={W - 16} y={H - 4} textAnchor="end" className="fill-zinc-500" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', letterSpacing: 2 }}>FREQUENCY (HZ) →</text>
        <text x={12} y={(pt + base) / 2} transform={`rotate(-90 12 ${(pt + base) / 2})`} textAnchor="middle" className="fill-zinc-500" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', letterSpacing: 2 }}>ENERGY →</text>

        <motion.path d={pro.area} fill="url(#ss-blue)" {...fill(0.5)} />
        <motion.path d={hex.area} fill="url(#ss-red)" {...fill(0.3)} />
        <motion.path d={pro.line} fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinejoin="round" {...draw(0.15)} />
        <motion.path d={hex.line} fill="none" stroke={RED} strokeWidth="2.5" strokeLinejoin="round" {...draw(0)} />

        {/* centroid markers */}
        <motion.g {...fill(1.2)}>
          <line x1={x(785)} x2={x(785)} y1={pt - 6} y2={base} stroke={RED} strokeWidth="1.5" strokeDasharray="4 4" />
          <line x1={x(908)} x2={x(908)} y1={pt - 6} y2={base} stroke={BLUE} strokeWidth="1.5" strokeDasharray="4 4" />
          <rect x={x(785) - 58} y={pt - 26} width="56" height="18" rx="9" fill={RED} />
          <text x={x(785) - 30} y={pt - 13} textAnchor="middle" fill="#fff" style={{ fontSize: 10, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>785 Hz</text>
          <rect x={x(908) + 2} y={pt - 26} width="56" height="18" rx="9" fill={BLUE} />
          <text x={x(908) + 30} y={pt - 13} textAnchor="middle" fill="#fff" style={{ fontSize: 10, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>908 Hz</text>
          {/* gap between centroids */}
          <line x1={x(785)} x2={x(908)} y1={pt - 36} y2={pt - 36} stroke="rgba(255,255,255,0.45)" />
          <line x1={x(785)} x2={x(785)} y1={pt - 40} y2={pt - 32} stroke="rgba(255,255,255,0.45)" />
          <line x1={x(908)} x2={x(908)} y1={pt - 40} y2={pt - 32} stroke="rgba(255,255,255,0.45)" />
          <text x={(x(785) + x(908)) / 2} y={pt - 41} textAnchor="middle" fill="rgba(255,255,255,0.75)" style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>Δ 123 Hz</text>
        </motion.g>
      </svg>
      <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
          <span className="flex items-center gap-2 text-xs whitespace-nowrap" data-testid="sound-legend-hexcore"><span className="w-3 h-3 rounded-sm" style={{ background: RED }} /> <span className="font-heading font-bold uppercase tracking-wider" style={{ color: RED }}>HexCore</span> <span className="text-zinc-400">lower-centred · 785 Hz</span></span>
          <span className="flex items-center gap-2 text-xs whitespace-nowrap" data-testid="sound-legend-profoam"><span className="w-3 h-3 rounded-sm" style={{ background: BLUE }} /> <span className="font-heading font-bold uppercase tracking-wider" style={{ color: BLUE }}>ProFoam</span> <span className="text-zinc-400">higher-centred · 908 Hz</span></span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Tone of the impact sound · lower = deeper · illustration</span>
      </div>
    </div>
  );
};

// 16 mm face-and-core sandwich, exploded cross-section
export const SandwichDiagram = ({ variant }) => {
  const p = PADDLES[variant];
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const isHex = variant === 'hexcore';
  const layer = (i, delay) => ({
    initial: { y: 18 * (i - 1), opacity: 0 },
    animate: inView ? { y: 0, opacity: 1 } : {},
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });
  return (
    <div ref={ref} className="relative" data-testid={`sandwich-${variant}`}>
      <svg viewBox="0 0 320 220" className="w-full h-auto" role="img" aria-label={`${p.name} 16 mm face-and-core sandwich`}>
        <defs>
          <pattern id={`carbon-${variant}`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="8" height="8" fill="#1a1a1d" /><rect width="4" height="8" fill="#2a2a2f" />
          </pattern>
          <pattern id={`hex-${variant}`} width="18" height="15.6" patternUnits="userSpaceOnUse">
            <polygon points="9,0 18,4.5 18,12 9,15.6 0,12 0,4.5" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          </pattern>
        </defs>
        {/* top carbon face */}
        <motion.g {...layer(0, 0.1)}>
          <polygon points="40,52 230,52 280,22 90,22" fill={`url(#carbon-${variant})`} stroke="rgba(255,255,255,0.35)" />
          <polygon points="40,52 230,52 230,60 40,60" fill="#111" stroke="rgba(255,255,255,0.25)" />
          <polygon points="230,52 280,22 280,30 230,60" fill="#0c0c0e" stroke="rgba(255,255,255,0.25)" />
        </motion.g>
        {/* core */}
        <motion.g {...layer(1, 0.3)}>
          <polygon points="40,84 230,84 280,54 90,54" fill={isHex ? '#f3f3ee' : '#2b2320'} stroke="rgba(255,255,255,0.3)" />
          {isHex && <polygon points="40,84 230,84 280,54 90,54" fill={`url(#hex-${variant})`} opacity="0.9" />}
          <polygon points="40,84 230,84 230,140 40,140" fill={isHex ? '#e6e6df' : '#352b27'} stroke="rgba(255,255,255,0.3)" />
          <polygon points="230,84 280,54 280,110 230,140" fill={isHex ? '#cfcfc6' : '#241d1a'} stroke="rgba(255,255,255,0.3)" />
          {isHex
            ? Array.from({ length: 9 }).map((_, i) => <line key={i} x1={48 + i * 21} x2={48 + i * 21} y1="84" y2="140" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" />)
            : Array.from({ length: 3 }).map((_, i) => <rect key={i} x={70 + i * 55} y="98" width="22" height="30" rx="2" fill="#15110f" stroke="rgba(255,255,255,0.15)" />)}
        </motion.g>
        {/* bottom carbon face */}
        <motion.g {...layer(2, 0.5)}>
          <polygon points="40,164 230,164 280,134 90,134" fill={`url(#carbon-${variant})`} stroke="rgba(255,255,255,0.35)" />
          <polygon points="40,164 230,164 230,172 40,172" fill="#111" stroke="rgba(255,255,255,0.25)" />
          <polygon points="230,164 280,134 280,142 230,172" fill="#0c0c0e" stroke="rgba(255,255,255,0.25)" />
        </motion.g>
        {/* dimension */}
        <motion.g initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 1 }}>
          <line x1="300" x2="300" y1="22" y2="172" stroke={p.accent} strokeWidth="1.5" />
          <line x1="294" x2="306" y1="22" y2="22" stroke={p.accent} strokeWidth="1.5" />
          <line x1="294" x2="306" y1="172" y2="172" stroke={p.accent} strokeWidth="1.5" />
          <text x="312" y="101" fill={p.accent} style={{ fontSize: 13, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }} transform="rotate(90 312 101)" textAnchor="middle">16 mm</text>
          <text x="40" y="204" fill="rgba(255,255,255,0.7)" style={{ fontSize: 9.5, fontFamily: 'ui-monospace, monospace', letterSpacing: 1.2 }}>CARBON · {isHex ? 'PP HONEYCOMB' : 'FOAM CORE'} · CARBON</text>
        </motion.g>
      </svg>
    </div>
  );
};
